import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import Editor from "../../../../../components/editor/Editor";
import ChannelTags from "../../../../../components/pickers/channel-tags/ChannelTags";
import SlackBots from "../../../../../components/pickers/slack-bots/SlackBots";
import Select from "../../../../../components/select/Select";
import TagList from "../../../../../components/tag-list/TagList";
import useRouting, { Link } from "../../../../../contexts/Routing";
import {
    ChannelMode, ChatSettingsSlack, ChatSettingsSlackInput,
} from "../../../../../__generated__/api";
import ChannelModeHelp from "../../channels/slack/ChannelModeHelp";
import EmojiActions from "../../channels/slack/EmojiActions";
import TicketCreateHelp from "../../channels/slack/TicketCreateHelp";

const CHANNEL_MODES = {
    [ChannelMode.Threaded]: "Threaded",
    [ChannelMode.SingleThread]: "One ticket at a time",
    [ChannelMode.Manual]: "Manual",
};

export interface Props {
    data: ChatSettingsSlack | undefined,
    onChange: (options: ChatSettingsSlackInput) => void;
    shouldAllowAutomation?: boolean;
    canAddTags: boolean;
}

export default function SlackSettings({
    data, onChange, shouldAllowAutomation, canAddTags,
}: Props): ReactElement {
    const { Paths } = useRouting();

    const [tags, setTags] = useState(() => data?.tags ?? []);
    const [channelMode, setChannelMode] = useState(() => data?.channelMode);
    const [approvedBotIds, setApprovedBotIds] = useState(() => data?.approvedBotIds ?? []);

    const [ignoreTicketsFromEmailDomains, setIgnoreTicketsFromEmailDomains] = useState(() => data?.ignoreTicketsFromEmailDomains);
    const [channelName, setChannelName] = useState(() => data?.name);
    const [ticketCreatedTemplate, setTicketCreatedTemplate] = useState(() => data?.ticketCreatedTemplate);
    const [emojiActions, setEmojiActions] = useState(() => data?.emojiActions ?? []);

    useEffect(() => {
        if (!data) {
            return;
        }
        if (!tags.equals(data.tags ?? [])) {
            setTags(data.tags ?? []);
        }
        if (data.channelMode !== channelMode) {
            setChannelMode(data.channelMode);
        }
        if (!ignoreTicketsFromEmailDomains?.equals(data.ignoreTicketsFromEmailDomains ?? [])) {
            setIgnoreTicketsFromEmailDomains(data.ignoreTicketsFromEmailDomains);
        }
        if (data.name && channelName !== data.name) {
            setChannelName(name => data.name ?? name);
        }
        if (!approvedBotIds.equals(data.approvedBotIds ?? [])) {
            setApprovedBotIds(data.approvedBotIds ?? []);
        }
        if (data.emojiActions && !emojiActions.equals(data?.emojiActions)) {
            setEmojiActions(data.emojiActions);
        }
        setTicketCreatedTemplate(ticketCreatedTemplate);
    }, [data]);

    useEffect(() => {
        if (!data) {
            return;
        }
        onChange({
            tags, channelMode, ignoreTicketsFromEmailDomains, approvedBotIds, ticketCreatedTemplate, emojiActions,
        });
    }, [tags, channelMode, ignoreTicketsFromEmailDomains, approvedBotIds, ticketCreatedTemplate, emojiActions]);

    return (
        <>
            <div className="border p-3 rounded bg-white mt-4 mb-4">
                <h5 className="mb-3 text-light">
                    Slack
                    {" "}
                    {channelName ? `(#${channelName})` : ""}
                </h5>

                {canAddTags && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">
                            Channel Tags
                        </Form.Label>
                        <ChannelTags
                            placeholder="Please pick a list of channel tags"
                            isMulti
                            onSelected={selected => setTags(selected)}
                            value={tags}
                            canCreate
                        />
                        <Form.Text>
                            Which tags should be associated with this queue. To manage channel tags, visit
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channel-tags" }}
                                className="link-info text-decoration-none"
                            >
                                General Settings
                            </Link>
                            .
                        </Form.Text>
                    </Form.Group>
                )}

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Channel Mode
                        {" "}
                        <ChannelModeHelp />
                    </Form.Label>

                    <Select
                        className="text-gray"
                        placeholder=""
                        onChange={value => setChannelMode(value.value as ChannelMode)}
                        options={Object.entries(CHANNEL_MODES).map(([value, label]) => ({ value, label }))}
                        value={!channelMode ? undefined : {
                            value: channelMode,
                            label: CHANNEL_MODES[channelMode],
                        }}
                    />
                    <Form.Text>
                        How do you when tickets to be created in this Slack channel?
                        <ChannelModeHelp buttonClassName="m-0 ms-1" />
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Ignore Internal Messages</Form.Label>
                    <TagList
                        placeholder="@example.com"
                        value={ignoreTicketsFromEmailDomains as string[] ?? []}
                        onChange={value => setIgnoreTicketsFromEmailDomains(value)}
                        canNotHaveDuplicates
                    />
                    <Form.Text>
                        Lets say your marketing team is always sending promotional content in
                        customer channels. You don&apos;t want a ticket created for each one. By adding
                        their email address or entire email domain (such as @example.com) into this
                        box, we will not create a ticket for any message that match a users email or email domain.
                        This setting overrides the same setting in the general configuration.
                        If any domains or emails are present in this list, we will ignore the general setting.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Approved Bots
                    </Form.Label>
                    <SlackBots
                        isMulti
                        placeholder="Which bot messages should not be ignored."
                        value={approvedBotIds as string[]}
                        onSelected={selected => setApprovedBotIds(selected)}
                    />
                    <Form.Text>
                        By default, Agent always blocks messages from other bots in Slack. However, you can
                        override this behavior and have Agent injest messages and create tickets from other
                        bots in your Slack workspace
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bolder">
                        Ticket Created Message Template
                        {" "}
                        <TicketCreateHelp />
                    </Form.Label>
                    <div className="bg-white border-light border rounded">
                        <Editor
                            text={ticketCreatedTemplate as string}
                            onChange={msg => {
                                setTicketCreatedTemplate(msg);
                            }}
                            isScrollDisabled
                            placeholder="New request created: {{SUBJECT}}"
                            shouldFocus={false}
                        />
                    </div>
                    <Form.Text>
                        When a ticket is created and a new thread is posted in the channel,
                        what information should this ticket contain?
                        <TicketCreateHelp buttonClassName="m-0 ms-1" />
                    </Form.Text>
                </Form.Group>

            </div>
            {shouldAllowAutomation && (
                <EmojiActions actions={emojiActions} onChange={setEmojiActions} />
            )}

        </>
    );
}

SlackSettings.defaultProps = {
    shouldAllowAutomation: false,
};
